import { useState } from "react";
import { Link } from "react-router-dom";
import { ErrorCodes, ProblemError, hasGrant, usePrincipal } from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import { Icon } from "../../ui/icons";
import { usePhoneIntake } from "./use-intake";
import type { ProductLine } from "./types";
import {
  PRODUCT_LINES,
  ProductFields,
  emptyProductFields,
  productFieldsInvalid,
  productFieldsToPayload,
  type ProductFieldValues,
} from "./product-fields";

// B0 — the phone lead-intake surface (Task #158: product-first, product-aware). The advisor,
// on a call, first picks the PRODUCT, then the fields below adapt to that product per the
// canonical [KAPI] field doc (product/specs/urun-bazli-bilgi-ihtiyaci.md). Caller basics +
// consent are common to every product. The advisor captures the caller's VERBAL consent to
// the versioned aydınlatma (CAPTURED_IN_INTERACTION) — a legal record, not a checkbox. No
// price, no offer at intake. RBAC is UX (the API enforces the party/lead/capture chain).

// FLAG: the aydınlatma notice version shown here is a displayed reference for the advisor
// to read on the call. The interaction contract carries NO noticeId/version field — the
// durable versioning is the server-derived LAWFUL_BASIS_MAP config version. Binding verbal
// consent to a specific notice version is a backend gap (flagged backend-first). A governed
// notices read would replace this constant.
const AYDINLATMA_NOTICE_VERSION = "v1";

// The per-product field set + its validation/payload mapping live in ./product-fields
// (shared with the Müşteri → Teklif Formu wizard so the two never diverge).

export function PhoneIntake() {
  const { t } = useT();
  const { me } = usePrincipal();
  const intake = usePhoneIntake();

  // The chain crosses three permissions; gate the affordance on all of them (the brief's
  // lead:write is the load-bearing one). The API enforces each call regardless.
  const canIntake =
    hasGrant(me, "lead:write", "own") &&
    hasGrant(me, "party:write", "own") &&
    hasGrant(me, "capture:write", "own");

  // Product-first: the advisor picks the line before anything else.
  const [line, setLine] = useState<ProductLine>("trafik");

  // Caller basics + common (canonical §0).
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [tckn, setTckn] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [il, setIl] = useState("");
  const [ilce, setIlce] = useState("");
  const [meslek, setMeslek] = useState("");

  // Consent (canonical §0 + §5 special-category).
  const [serviceConsent, setServiceConsent] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [healthConsent, setHealthConsent] = useState(false);

  // Product-specific field-set (vehicle/dwelling/travel/health) — shared with the wizard.
  const [fields, setFields] = useState<ProductFieldValues>(emptyProductFields);
  const patchFields = (patch: Partial<ProductFieldValues>) =>
    setFields((f) => ({ ...f, ...patch }));

  if (!canIntake) {
    return (
      <section className="surface">
        <div className="k-note">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--restricted">
              <Icon name="lock" size={18} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("intake.heading")}</p>
              <p role="note" className="k-note__desc" data-testid="intake-restricted">
                {t("intake.restricted")}
              </p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (intake.isSuccess) {
    return (
      <section className="surface">
        <div className="k-note" data-testid="intake-success">
          <div className="k-note__row">
            <span className="k-note__icon k-note__icon--success">
              <Icon name="check-circle" size={22} />
            </span>
            <div>
              <span className="k-badge k-badge--success">
                <Icon name="check" size={13} />
                {t("intake.doneBadge")}
              </span>
              <h1 className="k-note__title" style={{ marginTop: 10 }}>
                {t("intake.doneTitle")}
              </h1>
              <p className="k-note__desc">{t("intake.doneBody")}</p>
              <p className="k-note__desc">
                {t("intake.leadRef")}: {intake.data.leadId.slice(0, 8)}
              </p>
              {/* A3 — the partial-failure branch that used to live here is GONE: the
                  state it reported ("lead saved but the vehicle wasn't") cannot occur
                  inside one transaction. A repeat caller still surfaces, but through
                  the EXISTING dedup UX below (CSA-1009), which is the shipped
                  decision — the advisor is told and sent to the existing record
                  rather than having the call silently attached to it. */}
              <div className="k-note__actions">
                <button
                  type="button"
                  className="k-btn k-btn--secondary"
                  data-testid="intake-another"
                  onClick={() => intake.reset()}
                >
                  {t("intake.another")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  const isSaglik = line === "saglik";

  // Optional-but-encouraged: empty is fine; a filled product value must be valid to submit.
  const ready =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    phone.trim().length > 0 &&
    tckn.trim().length === 11 &&
    !productFieldsInvalid(line, fields) &&
    // Sağlık: the SEPARATE special-category açık rıza is required for this line.
    (!isSaglik || healthConsent) &&
    serviceConsent; // the required verbal consent gates submit

  return (
    <section className="surface">
      <header className="surface__head">
        <div>
          <h1 className="surface__title">{t("intake.heading")}</h1>
          <p className="surface__subtitle">{t("intake.subheading")}</p>
        </div>
      </header>

      <form
        className="intake-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!ready) return;
          intake.mutate({
            line,
            firstName,
            lastName,
            phone,
            email: email.trim() || undefined,
            tckn,
            birthDate: birthDate.trim() || undefined,
            il: il.trim() || undefined,
            ilce: ilce.trim() || undefined,
            meslek: meslek.trim() || undefined,

            serviceConsent,
            marketingConsent,
            // Product-specific field-set (vehicle/dwelling/travel/health) — line-conditioned
            // by the shared mapper; the hook re-validates + skips the asset when absent/invalid.
            ...productFieldsToPayload(line, fields),
            // Health — the separate açık rıza; all capture-only (offline until go-live).
            healthConsent: isSaglik ? healthConsent : undefined,
          });
        }}
      >
        {/* PRODUCT-FIRST — the advisor picks the line before anything else; the fields below
            adapt to it (product-aware, canonical [KAPI] subset). No price at intake. */}
        <div className="k-card" data-testid="product-section">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.productSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.line")}</span>
            <span className="k-select">
              <select
                className="k-select__native"
                data-testid="in-line"
                value={line}
                onChange={(e) => setLine(e.target.value as ProductLine)}
              >
                {PRODUCT_LINES.map((code) => (
                  <option key={code} value={code}>
                    {t(`intake.line.${code}` as `intake.line.${ProductLine}`)}
                  </option>
                ))}
              </select>
              <span className="k-select__chevron">
                <Icon name="chevron-down" size={16} />
              </span>
            </span>
          </label>
          {/* GAP-AUDIT-3 D10 — the free-text "Not" input was removed, not wired.
              It was collected into the payload and then dropped: useIntake never
              read `notes`, and there is nowhere within the frozen /v1 contracts to
              put it — recordInteractionSchema has no note/summary field, and the
              intent's `subjectRef` is a guard-restricted slot for structured
              metadata, not free prose an advisor might type a health detail into.
              The structured-note schema is an explicit, unbuilt spec gap
              (crm-operational-layer §8) and that is its real home. Same ruling as
              the Dalga-1 web "Ara/Mesaj" buttons: a control that silently discards
              what the user typed is worse than no control. */}
          <p className="k-field__hint" data-testid="no-price-note">
            {t("intake.noPriceNote")}
          </p>
        </div>

        {/* Caller basics — common to every product (canonical §0). */}
        <div className="k-card">
          <p className="surface__section-label" style={{ margin: "0 0 16px" }}>
            {t("intake.callerSection")}
          </p>
          <label className="k-field">
            <span className="k-field__label">{t("intake.firstName")}</span>
            <input
              className="k-input"
              data-testid="in-first"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.lastName")}</span>
            <input
              className="k-input"
              data-testid="in-last"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.tckn")}</span>
            <input
              className="k-input"
              data-testid="in-tckn"
              inputMode="numeric"
              value={tckn}
              onChange={(e) => setTckn(e.target.value.replace(/\D/g, "").slice(0, 11))}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.birthDate")}</span>
            <input
              className="k-input"
              data-testid="in-birthdate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.phone")}</span>
            <input
              className="k-input"
              data-testid="in-phone"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.email")}</span>
            <input
              className="k-input"
              data-testid="in-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.meslek")}</span>
            <input
              className="k-input"
              data-testid="in-meslek"
              value={meslek}
              onChange={(e) => setMeslek(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.il")}</span>
            <input
              className="k-input"
              data-testid="in-il"
              value={il}
              onChange={(e) => setIl(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("intake.ilce")}</span>
            <input
              className="k-input"
              data-testid="in-ilce"
              value={ilce}
              onChange={(e) => setIlce(e.target.value)}
            />
          </label>
          <p className="k-field__hint" data-testid="common-capture-note">
            {t("intake.commonCaptureNote")}
          </p>
        </div>

        {/* Product-aware field-set (vehicle/dwelling/travel/health) — shared with the
            Müşteri → Teklif Formu wizard so the two never diverge (canonical [KAPI] subset). */}
        <ProductFields line={line} values={fields} onChange={patchFields} />

        {/* Verbal-consent capture — a legal record, not a stray checkbox: a governed
            (calm amber) card holding the versioned aydınlatma reference + attestations. */}
        <div className="k-card k-card--accent-amber" data-testid="consent-capture">
          <div className="k-note__row" style={{ marginBottom: 16 }}>
            <span className="k-note__icon k-note__icon--restricted">
              <Icon name="lock" size={18} />
            </span>
            <div>
              <p className="k-note__title k-note__title--sm">{t("intake.consentSection")}</p>
              <p className="k-note__desc" data-testid="notice-version">
                {t("intake.noticeRef")} — {t("intake.noticeVersionLabel")}:{" "}
                {AYDINLATMA_NOTICE_VERSION}
              </p>
            </div>
          </div>
          <label className="k-choice" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              data-testid="consent-service"
              checked={serviceConsent}
              onChange={(e) => setServiceConsent(e.target.checked)}
            />
            <span className="k-check">
              <Icon name="check" size={14} />
            </span>
            {t("intake.serviceAttest")}
          </label>
          <label className="k-choice" style={{ marginBottom: 12 }}>
            <input
              type="checkbox"
              data-testid="consent-marketing"
              checked={marketingConsent}
              onChange={(e) => setMarketingConsent(e.target.checked)}
            />
            <span className="k-check">
              <Icon name="check" size={14} />
            </span>
            {t("intake.marketingAttest")}
          </label>
          {/* Sağlık — a SEPARATE special-category açık rıza (canonical §5). Required for the
              sağlık line; capture-only (no special-category intent type — backend-first). */}
          {isSaglik ? (
            <>
              <label className="k-choice" style={{ marginBottom: 8 }}>
                <input
                  type="checkbox"
                  data-testid="consent-health"
                  checked={healthConsent}
                  onChange={(e) => setHealthConsent(e.target.checked)}
                />
                <span className="k-check">
                  <Icon name="check" size={14} />
                </span>
                {t("intake.healthAttest")}
              </label>
              <p className="k-field__hint" data-testid="health-consent-note">
                {t("intake.healthConsentNote")}
              </p>
            </>
          ) : null}
          <p className="k-field__hint" data-testid="iys-note">
            {t("intake.iysNote")}
          </p>
        </div>

        {/* PARTY-DEDUP — a structured 409 (CSA-1009) means this phone/TCKN already belongs
            to a customer. Turn the dead-end into a helpful next step: point to the search
            (U1-F) instead of a generic error. */}
        {intake.error instanceof ProblemError &&
        intake.error.is(ErrorCodes.PARTY_ALREADY_EXISTS) ? (
          <div role="note" className="k-note" data-testid="intake-dedup">
            <div className="k-note__row">
              <span className="k-note__icon k-note__icon--restricted">
                <Icon name="info" size={18} />
              </span>
              <div>
                <p className="k-note__desc">{t("intake.dedup")}</p>
                <div className="k-note__actions">
                  <Link
                    to="/households"
                    className="k-btn k-btn--secondary k-btn--sm"
                    data-testid="intake-dedup-search"
                  >
                    <Icon name="search" size={15} />
                    {t("intake.dedupSearch")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : intake.isError ? (
          <p role="alert" className="k-field__error" data-testid="intake-error">
            <Icon name="alert-triangle" size={14} />
            {t("intake.error")}
          </p>
        ) : null}

        <button
          type="submit"
          className="k-btn k-btn--primary"
          data-testid="intake-submit"
          disabled={!ready || intake.isPending}
        >
          <Icon name="check" size={16} />
          {t("intake.submit")}
        </button>
      </form>
    </section>
  );
}
